'use client'

import { StarRatingBar } from '@lastbrand/web-ui/star-rating-bar'
import { css } from '@lastbrand/web-ui-styled-system/css'
import { useTranslation } from 'next-i18next'
import { useMemo } from 'react'

import { RichTextDS } from '@src/components/blocks/rich-text-ds'
import { ProductCardLink } from '@src/components/product-card/product-card-actions/product-card-link/product-card-link'
import { ProductCardHoverProvider } from '@src/components/product-card/product-card-context/product-card-hover.context'
import {
  getImagesForOptions,
  getInitialSelectedVariant,
  getVariantTags,
} from '@src/components/product-card/product-card.helpers'
import type { IProductCard } from '@src/components/product-card/product-card.types'
import { AddToCartButton, addToCartButtonHoverRevealParentCss } from '@src/ds/blocks/add-to-cart-button'
import { AddToWishlistButton } from '@src/ds/blocks/add-to-wishlist-button'
import { ImageTile, productTileImageHoverSwapCss } from '@src/ds/blocks/image-tile'
import type { TrackConfig } from '@src/quince-components/src/hocs/withTracking'
import { NO_COLOR_OPTION_NAME } from '@src/quince-site-common/src/constants/app'
import type { GenderName } from '@src/quince-site-common/src/utils/getCurrentPageGenderContext'
import { toStarRatingValue } from '@src/utils/star-rating.utils'

import type { Document } from '@contentful/rich-text-types'

export interface ProductReviewCardDsProps {
  productCard: IProductCard
  /** Average rating (0–5). Rounded to nearest 0.5 for the DS star component. */
  rating?: number
  /** Raw Contentful Document for the review description text. */
  descriptionContent?: Document
  /** Raw Contentful Document for the reviewer name / author text. */
  authorContent?: Document
  tracker?: string
  productClickTrackConfig?: TrackConfig
  wishlistTrackConfig?: Record<string, unknown>
  eagerLoadImage?: boolean
  /** Gender context for image resolution (matches DS product card). */
  gender?: GenderName
}

const footerClass = css({
  display: 'flex',
  flexDirection: 'column',
  pt: { base: 'space-12', lg: 'space-16' },
})

const descriptionClass = css({
  textStyle: { base: 'body-12', lg: 'body-14' },
  color: 'black',
  marginBottom: 'space-8',
  marginTop: { base: 'space-8', lg: 'space-12' },
})

const authorClass = css({
  textStyle: { base: 'body-10-strong', lg: 'body-12-strong' },
  color: 'black',
})

export interface ProductReviewCardDsFooterProps {
  rating?: number
  descriptionContent?: Document
  authorContent?: Document
}

/**
 * The review footer (stars + description + author), extracted so the SVD fork can reuse it verbatim
 * as `SvdProductCard`'s `customFooter`. Sourced purely from CMS review content — it never touches
 * product fields, so it is shape-agnostic and safe to share across both card bodies. Mirrors the
 * shared `footer` in the legacy fork (`SectionRenderer/sections/honestReviewsV2/HonestReviewsV2.tsx`).
 */
export function ProductReviewCardDsFooter({
  rating,
  descriptionContent,
  authorContent,
}: ProductReviewCardDsFooterProps) {
  const { t } = useTranslation('common')
  return (
    <div className={footerClass}>
      {rating !== undefined &&
        (() => {
          // aria-label announces the same rounded value the stars show.
          const starValue = toStarRatingValue(rating)
          return <StarRatingBar value={starValue} aria-label={t('out_of_5_stars', { rating: parseFloat(starValue) })} />
        })()}
      {descriptionContent && <RichTextDS content={descriptionContent} className={descriptionClass} />}
      {authorContent && <RichTextDS content={authorContent} className={authorClass} />}
    </div>
  )
}

function ProductReviewCardDsInner({
  productCard,
  rating,
  descriptionContent,
  authorContent,
  tracker,
  productClickTrackConfig,
  wishlistTrackConfig,
  eagerLoadImage,
  gender,
}: ProductReviewCardDsProps) {
  const product = productCard.product
  const { slug, relatedIds, flags, title, cardVariants } = product

  const selectedVariant = useMemo(() => getInitialSelectedVariant({ cardVariants }), [cardVariants])

  const rawImages = useMemo(() => getImagesForOptions(selectedVariant, gender), [selectedVariant, gender])

  const { applicableTags } = useMemo(() => getVariantTags(selectedVariant), [selectedVariant])

  // `?.` on `displayConfig` too, not just on `selectedVariant`: the legacy type declares it required,
  // but an SVD no-VDA card carries `displayConfig: null` at runtime. This component is only reached
  // with legacy-shaped data (the SVD fork routes to `SvdProductCard` in `honest-reviews-ds.tsx`), so
  // this is defence-in-depth — it builds its own tile and therefore has no shared-wrapper guard to
  // fall back on, and a throw here takes down the whole section via `SectionRenderer`'s boundary.
  // The type-aware rule sees the guard as redundant because the legacy type hides that runtime null.
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const colorDisplayName = selectedVariant?.displayConfig?.displayName
  const variantFallbackAlt = [colorDisplayName, title].filter(Boolean).join(' ')
  // Same rule as the DS card's `productLinkAriaLabel`: the "No Color" placeholder is not read out.
  const exposeColorToScreenReader =
    !!colorDisplayName && colorDisplayName.toLowerCase() !== NO_COLOR_OPTION_NAME.toLowerCase()
  const productLinkAriaLabel = exposeColorToScreenReader ? `${colorDisplayName} ${title}` : title

  // This card renders no title link, so the image overlay is the only keyboard path to the PDP: it
  // must be a tab stop, named, and never inside aria-hidden (WCAG 2.1.1 / 4.1.2) — except when the
  // variant has no price and the link is a dead `javascript:void(0)`, which stays out of the tab order.
  // The ring is inset because the image area clips.
  const imageOverlay = (
    <div
      className={css({
        position: 'absolute',
        inset: 0,
        zIndex: 1,
        '& a': { position: 'absolute', inset: 0, display: 'block' },
        '& a:focus-visible': { outline: '2px solid', outlineColor: 'black', outlineOffset: '-2px' },
      })}
    >
      <ProductCardLink
        tabIndex={selectedVariant?.price?.salePrice ? 0 : -1}
        aria-label={productLinkAriaLabel}
        slug={slug}
        tracker={tracker}
        productCardLinkClass={css({ display: 'block', textDecoration: 'none' })}
        selectedCardVariant={selectedVariant}
        title={title}
        spid={relatedIds.spid}
        reviewSummary={product.reviews}
        productClickTrackConfig={productClickTrackConfig}
        colorVariants={product.colorVariants}
        product={product}
      >
        <span />
      </ProductCardLink>
    </div>
  )

  return (
    <article
      data-testid="product-review-card-ds"
      data-slug={slug}
      className={css(
        { display: 'flex', flexDirection: 'column', width: '100%' },
        productTileImageHoverSwapCss,
        flags.quickBuyEnabled !== false ? addToCartButtonHoverRevealParentCss : {},
      )}
    >
      <div data-slot="product-tile-hover-image-area" className={css({ position: 'relative', width: '100%' })}>
        <ImageTile
          images={rawImages}
          variantFallbackAlt={variantFallbackAlt}
          applicableTags={applicableTags}
          readyToShipEnabled={Boolean(flags.readyToShipEnabled)}
          imageLoading={eagerLoadImage ? 'eager' : 'lazy'}
        />
        {imageOverlay}
        <AddToWishlistButton
          spid={relatedIds.spid}
          // Same runtime-null guard as `colorDisplayName` above.
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          selectedColor={selectedVariant?.displayConfig?.value ?? ''}
          wishlistTrackConfig={wishlistTrackConfig}
          wishlistEnabled={flags.wishlistEnabled !== false}
          productName={variantFallbackAlt}
          // Honest Reviews can repeat the same product across cards; opt out of cross-instance
          // wishlist sync so each card only reflects its own toggle (STRFRNT-16247).
          syncAcrossInstances={false}
        />
        <AddToCartButton
          productCard={productCard}
          selectedVariant={selectedVariant}
          productClickTrackConfig={productClickTrackConfig}
          visible={flags.quickBuyEnabled !== false}
          // eslint-disable-next-line i18next/no-literal-string -- reveal mode token
          addToBagReveal="hover"
        />
      </div>

      <ProductReviewCardDsFooter
        rating={rating}
        descriptionContent={descriptionContent}
        authorContent={authorContent}
      />
    </article>
  )
}

export function ProductReviewCardDs(props: ProductReviewCardDsProps) {
  return (
    <ProductCardHoverProvider>
      <ProductReviewCardDsInner {...props} />
    </ProductCardHoverProvider>
  )
}

export default ProductReviewCardDs
